import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ArtifactProcess } from '../../types/artifactProcess';
import {
  ArtifactProcessService,
  ProcessCArtifact,
} from '../../services/api/artifactProcessService';
import { ArtifactService } from '../../services/api/artifactService';
import { PrimeArtifactService } from '../../services/primeArtifactService';
import AddArtifactDialog from './AddArtifactDialog';
import styles from './ArtifactProcessPage.module.css';

interface ArtifactProcessPageProps {
  artifactId?: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const ArtifactProcessPage: React.FC<ArtifactProcessPageProps> = ({ artifactId }) => {
  const [searchParams] = useSearchParams();
  const [artifactProcess, setArtifactProcess] = useState<ArtifactProcess | null>(null);
  const [artifactName, setArtifactName] = useState<string>('');
  const [primeProcessName, setPrimeProcessName] = useState<string>('');
  const [artifactTreeId, setArtifactTreeId] = useState<string>('');
  const [cArtifacts, setCArtifacts] = useState<ProcessCArtifact[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProcessDetails, setShowProcessDetails] = useState(false);
  const [showAddArtifactDialog, setShowAddArtifactDialog] = useState(false);

  const loadArtifactProcess = useCallback(async (processId: string) => {
    try {
      setLoading(true);
      setError(null);
      console.log('[ArtifactProcessPage] Loading artifact process with ID:', processId);
      const process = await ArtifactProcessService.getById(processId);
      console.log('[ArtifactProcessPage] Loaded artifact process:', process);
      setArtifactProcess(process);

      // Load artifact and prime process names
      if (process.artifact_ref) {
        try {
          const artifact = await ArtifactService.getById(process.artifact_ref);
          if (artifact) {
            setArtifactName(artifact.name);
            setArtifactTreeId(artifact.artifact_tree_id || '');
          } else {
            setArtifactName(process.artifact_ref);
          }
        } catch (err) {
          console.warn('Failed to load artifact name:', err);
          setArtifactName(process.artifact_ref);
        }
      }

      if (process.prime_process_ref) {
        try {
          const primeProcess = await PrimeArtifactService.getById(process.prime_process_ref);
          setPrimeProcessName(primeProcess.name);
        } catch (err) {
          console.warn('Failed to load prime process name:', err);
          // Show a shortened version of the ID for better UX
          setPrimeProcessName(`Prime Process (${process.prime_process_ref.substring(0, 8)}...)`);
        }
      }

      // Load c-artifacts for this process
      await loadCArtifacts(processId);
    } catch (err) {
      console.error('Error loading artifact process:', err);
      setError('Failed to load the artifact process');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const processId = searchParams.get('processId');
    console.log('[ArtifactProcessPage] Received processId:', processId);
    if (processId) {
      void loadArtifactProcess(processId);
    }
  }, [searchParams, loadArtifactProcess]);

  const loadCArtifacts = async (processId: string) => {
    try {
      const cArtifactsData = await ArtifactProcessService.getCArtifacts(processId);
      setCArtifacts(cArtifactsData);
      console.log('[ArtifactProcessPage] Loaded c-artifacts:', cArtifactsData);
    } catch (err) {
      console.warn('Failed to load c-artifacts:', err);
      setCArtifacts([]);
    }
  };

  const handleAddArtifactSuccess = async () => {
    const processId = searchParams.get('processId');
    if (processId) {
      await loadCArtifacts(processId);
    }
  };

  if (loading) {
    return (
      <div className={styles.artifactProcessPage}>
        <div className={styles.loading}>Loading artifact process...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.artifactProcessPage}>
        <div className={styles.error}>{error}</div>
      </div>
    );
  }

  if (!artifactProcess) {
    return (
      <div className={styles.artifactProcessPage}>
        <div className={styles.noProcess}>No artifact process selected</div>
      </div>
    );
  }

  return (
    <div className={styles.artifactProcessPage}>
      <div className={styles.artifactProcessHeader}>
        <h1>{artifactProcess.name}</h1>
        <div className={styles.processMeta}>
          <span className={styles.processType}>
            Type: {artifactProcess.process_type || 'Not specified'}
          </span>
          <span className={styles.processStatus}>
            Status: {artifactProcess.process_status || 'Not specified'}
          </span>
          <span className={styles.demandFlowType}>
            Demand Flow: {artifactProcess.demand_flow_type || 'Not specified'}
          </span>
        </div>
      </div>

      {artifactProcess.description && (
        <div className={styles.artifactProcessDescription}>
          <h3>Description</h3>
          <p>{artifactProcess.description}</p>
        </div>
      )}

      <div className={styles.processDetailsToggle}>
        <button
          className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSm}`}
          onClick={() => setShowProcessDetails(!showProcessDetails)}
        >
          {showProcessDetails ? '▼' : '▶'} Process Details
        </button>
      </div>

      {showProcessDetails && (
        <div className={styles.artifactProcessDetails}>
          <div className={styles.detailSection}>
            <h3>Process Information</h3>
            <div className={styles.detailGrid}>
              <div className={styles.detailItem}>
                <label>Artifact Reference:</label>
                <span>{artifactName || artifactProcess.artifact_ref}</span>
              </div>
              <div className={styles.detailItem}>
                <label>Prime Process Reference:</label>
                <span>{primeProcessName || artifactProcess.prime_process_ref}</span>
              </div>
              <div className={styles.detailItem}>
                <label>Demand Type:</label>
                <span>{artifactProcess.demand_type || 'Not specified'}</span>
              </div>
              <div className={styles.detailItem}>
                <label>Is Active:</label>
                <span>{artifactProcess.is_active ? 'Yes' : 'No'}</span>
              </div>
            </div>
          </div>

          {artifactProcess.conversion_rate && (
            <div className={styles.detailSection}>
              <h3>Conversion Rates</h3>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <label>Rate:</label>
                  <span>{artifactProcess.conversion_rate}</span>
                </div>
                {artifactProcess.conversion_rate_minimum && (
                  <div className={styles.detailItem}>
                    <label>Minimum:</label>
                    <span>{artifactProcess.conversion_rate_minimum}</span>
                  </div>
                )}
                {artifactProcess.conversion_rate_maximum && (
                  <div className={styles.detailItem}>
                    <label>Maximum:</label>
                    <span>{artifactProcess.conversion_rate_maximum}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {artifactProcess.duration && (
            <div className={styles.detailSection}>
              <h3>Duration</h3>
              <div className={styles.detailGrid}>
                <div className={styles.detailItem}>
                  <label>Duration:</label>
                  <span>{artifactProcess.duration}</span>
                </div>
                {artifactProcess.duration_minimum && (
                  <div className={styles.detailItem}>
                    <label>Minimum:</label>
                    <span>{artifactProcess.duration_minimum}</span>
                  </div>
                )}
                {artifactProcess.duration_maximum && (
                  <div className={styles.detailItem}>
                    <label>Maximum:</label>
                    <span>{artifactProcess.duration_maximum}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {artifactProcess.process_parameters &&
            Object.keys(artifactProcess.process_parameters).length > 0 && (
              <div className={styles.detailSection}>
                <h3>Process Parameters</h3>
                <pre className={styles.jsonDisplay}>
                  {JSON.stringify(artifactProcess.process_parameters, null, 2)}
                </pre>
              </div>
            )}

          {artifactProcess.validation_rules &&
            Object.keys(artifactProcess.validation_rules).length > 0 && (
              <div className={styles.detailSection}>
                <h3>Validation Rules</h3>
                <pre className={styles.jsonDisplay}>
                  {JSON.stringify(artifactProcess.validation_rules, null, 2)}
                </pre>
              </div>
            )}

          {artifactProcess.demand_calculation_rules &&
            Object.keys(artifactProcess.demand_calculation_rules).length > 0 && (
              <div className={styles.detailSection}>
                <h3>Demand Calculation Rules</h3>
                <pre className={styles.jsonDisplay}>
                  {JSON.stringify(artifactProcess.demand_calculation_rules, null, 2)}
                </pre>
              </div>
            )}
        </div>
      )}

      <div className={styles.cArtifactsSection}>
        <div className={styles.sectionHeader}>
          <h3>Artifacts in Process</h3>
          <button className={`${styles.btn} ${styles.btnPrimary} ${styles.btnSm}`} onClick={() => setShowAddArtifactDialog(true)}>
            + Add Artifact
          </button>
        </div>

        {cArtifacts.length === 0 ? (
          <div className={styles.noCArtifacts}>
            <p>No artifacts found for this process.</p>
            <p>Add artifacts to define the process flow with their input and output states.</p>
          </div>
        ) : (
          <div className={styles.cArtifactsList}>
            {cArtifacts.map((cArtifact) => (
              <div key={cArtifact.artifact_id} className={styles.cArtifactCard}>
                <div className={styles.cArtifactHeader}>
                  <h4>{cArtifact.artifact_name}</h4>
                  <span className={styles.artifactId}>ID: {cArtifact.artifact_id}</span>
                </div>

                <div className={styles.cArtifactContent}>
                  <div className={styles.statesSection}>
                    <h5>States ({cArtifact.states.length})</h5>
                    {cArtifact.states.length === 0 ? (
                      <p className={styles.noStates}>No states defined</p>
                    ) : (
                      <div className={styles.statesList}>
                        {cArtifact.states.map((state) => (
                          <div key={state.id} className={styles.stateItem}>
                            <span className={styles.stateName}>{state.name}</span>
                            <span className={styles.stateType}>{state.state_type}</span>
                            {state.is_initial && <span className={`${styles.badge} ${styles.initial}`}>Initial</span>}
                            {state.is_final && <span className={`${styles.badge} ${styles.final}`}>Final</span>}
                            {state.is_required && <span className={`${styles.badge} ${styles.required}`}>Required</span>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className={styles.transitionsSection}>
                    <h5>Transitions ({cArtifact.transitions.length})</h5>
                    {cArtifact.transitions.length === 0 ? (
                      <p className={styles.noTransitions}>No transitions defined</p>
                    ) : (
                      <div className={styles.transitionsList}>
                        {cArtifact.transitions.map((transition) => {
                          const inputState = cArtifact.states.find(
                            (s) => s.id === transition.input_state_ref
                          );
                          const outputState = cArtifact.states.find(
                            (s) => s.id === transition.output_state_ref
                          );
                          return (
                            <div key={transition.id} className={styles.transitionItem}>
                              <div className={styles.transitionFlow}>
                                <span className={styles.inputState}>{inputState?.name || 'Unknown'}</span>
                                <span className={styles.transitionArrow}>→</span>
                                <span className={styles.outputState}>
                                  {outputState?.name || 'Unknown'}
                                </span>
                              </div>
                              <div className={styles.transitionDetails}>
                                <span className={styles.transitionName}>{transition.name}</span>
                                <span className={styles.transitionType}>
                                  {transition.transition_type}
                                </span>
                                <span className={styles.ratio}>
                                  Ratio: {transition.input_output_ratio}
                                </span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.artifactProcessActions}>
        <button className={`${styles.btn} ${styles.btnPrimary}`}>Edit Process</button>
        <button className={`${styles.btn} ${styles.btnSecondary}`}>View States</button>
        <button className={`${styles.btn} ${styles.btnSecondary}`}>View Transitions</button>
      </div>

      {artifactProcess && artifactTreeId && (
        <AddArtifactDialog
          isOpen={showAddArtifactDialog}
          onClose={() => setShowAddArtifactDialog(false)}
          onSuccess={handleAddArtifactSuccess}
          processId={artifactProcess.id}
          processName={artifactProcess.name}
          artifactRef={artifactProcess.artifact_ref}
          artifactTreeId={artifactTreeId}
        />
      )}
    </div>
  );
};

export default ArtifactProcessPage;
